// Документ контента страницы тарифов — то, что правится из админки.
//
// Живёт он в базе: пустую базу засевает сервер (server/pricing/seed.mjs), а
// дальше документ правит админ. Но у каждой строки остаётся ключ словаря, и
// пустое поле значит «взять встроенный перевод», а не «показать пустоту».
//
// Фолбэк на словарь нужен ради языков. Без него первая же строка, заведённая
// в админке и не переведённая, оставила бы четыре языка с дырой на витрине.
//
// Порядок разрешения текста: документ на нужном языке → словарь → документ на
// языке по умолчанию → сам ключ. Словарь стоит раньше чужого языка нарочно:
// англичанину лучше показать встроенный английский, чем русский текст админа.
// Последний шаг — для строк, которых в словаре нет вовсе: их завёл админ, и
// кроме введённого им текста показать нечего.
import { DICTIONARIES, type Lang, type TranslationKey } from '../i18n';
import {
  EXTRAS,
  FEATURES,
  HIGHLIGHTS,
  KEY_FEATURES,
  PLANS,
  PRICE,
  SECTIONS,
  SOON,
  type Limit,
  type PlanId,
} from './plans';

export const DOC_VERSION = 1;

/** Язык, на который откатываемся, когда своего перевода нет. */
export const FALLBACK_LANG: Lang = 'ru';

/** Текст по языкам. Отсутствующий или пустой язык — повод пойти в фолбэк. */
export type LocalizedText = Partial<Record<Lang, string>>;

export interface DocSection {
  id: string;
  /** Ключ словаря со встроенным переводом; нет — строку завели в админке. */
  titleKey?: TranslationKey;
  title: LocalizedText;
  hintKey?: TranslationKey;
  hint: LocalizedText;
}

export interface DocFeature {
  id: string;
  section: string;
  /** Выключенная строка остаётся в документе, но со страницы пропадает. */
  on: boolean;
  titleKey?: TranslationKey;
  title: LocalizedText;
  free: Limit;
  pro: Limit;
}

export interface DocItem {
  id: string;
  titleKey?: TranslationKey;
  title: LocalizedText;
}

export interface DocExtra extends DocItem {
  price: number;
  hintKey?: TranslationKey;
  hint: LocalizedText;
}

export interface DocFaq {
  id: string;
  questionKey?: TranslationKey;
  question: LocalizedText;
  answerKey?: TranslationKey;
  answer: LocalizedText;
}

export interface PricingDoc {
  v: number;
  price: { currency: string; monthly: number; yearly: number };
  sections: DocSection[];
  features: DocFeature[];
  /** Идентификаторы строк, вынесенных в карточку тарифа. */
  highlights: Record<PlanId, string[]>;
  soon: DocItem[];
  extras: DocExtra[];
  faq: DocFaq[];
  /** Переопределения общих текстов страницы, ключ словаря → текст по языкам. */
  texts: Record<string, LocalizedText>;
}

/**
 * Тексты страницы, которые правятся из админки.
 *
 * Сюда попадает то, что можно переписать, не сломав смысл интерфейса:
 * заголовки, обещания, подписи кнопок. Подписи колонок и словарь значений
 * («без лимита», «есть», «— ») остаются в коде: это не контент, а лексика
 * самой таблицы, и её правка ломает страницу, а не улучшает.
 */
export const EDITABLE_TEXTS: TranslationKey[] = [
  'pricing.title',
  'pricing.subtitle',
  // Заголовок вкладки и описание для выдачи — отдельно от заголовка на самой
  // странице. На странице над ценами уместно короткое «Тарифы», а в поиске по
  // одному слову не понять, чьи это тарифы и что там внутри.
  'pricing.seo.title',
  'pricing.seo.description',
  'pricing.plan.free',
  'pricing.plan.free.tagline',
  'pricing.plan.pro',
  'pricing.plan.pro.tagline',
  'pricing.cta.free',
  'pricing.cta.pro',
  'pricing.cta.note',
  'pricing.compare',
  'pricing.extras.title',
  'pricing.extras.hint',
  'pricing.soon.title',
  'pricing.soon.hint',
  'pricing.faq.title',
];

const SECTION_HINT_KEY: Partial<Record<string, TranslationKey>> = {
  ai: 'pricing.section.ai.hint',
  watch: 'pricing.section.watch.hint',
};

const FAQ_IDS = ['1', '2', '3', '4'] as const;

function emptyTexts(): LocalizedText {
  return {};
}

/**
 * Документ «как в коде»: структура заполнена, текст берётся из словарей.
 *
 * Это запасной вариант страницы, а не то, что обычно показывается: настоящий
 * документ приезжает с сервера. Он нужен, пока запрос в пути, и если сервер
 * не ответил вовсе — витрина обязана нарисоваться в любом случае.
 */
export function defaultPricingDoc(): PricingDoc {
  return {
    v: DOC_VERSION,
    price: { currency: PRICE.currency, monthly: PRICE.monthly, yearly: PRICE.yearly },
    sections: SECTIONS.map((id) => ({
      id,
      titleKey: `pricing.section.${id}` as TranslationKey,
      title: emptyTexts(),
      hintKey: SECTION_HINT_KEY[id],
      hint: emptyTexts(),
    })),
    features: FEATURES.map((f) => ({
      id: f.id,
      section: f.section,
      on: true,
      titleKey: f.titleKey,
      title: emptyTexts(),
      free: f.free,
      pro: f.pro,
    })),
    highlights: {
      free: HIGHLIGHTS.free.map(keyToFeatureId),
      pro: HIGHLIGHTS.pro.map(keyToFeatureId),
    },
    soon: SOON.map((key) => ({
      id: key.replace('pricing.soon.', ''),
      titleKey: key,
      title: emptyTexts(),
    })),
    extras: EXTRAS.map((e) => ({
      id: e.id,
      price: e.price,
      titleKey: e.titleKey,
      title: emptyTexts(),
      hintKey: e.hintKey,
      hint: emptyTexts(),
    })),
    faq: FAQ_IDS.map((n) => ({
      id: n,
      questionKey: `pricing.faq.q${n}` as TranslationKey,
      question: emptyTexts(),
      answerKey: `pricing.faq.a${n}` as TranslationKey,
      answer: emptyTexts(),
    })),
    texts: {},
  };
}

/** Ключ строки → её id: карточки тарифов ссылаются на строки по id. */
function keyToFeatureId(key: TranslationKey): string {
  return FEATURES.find((f) => f.titleKey === key)?.id ?? key;
}

/**
 * Встроенный перевод по ключу, без обращения к провайдеру i18n.
 *
 * Наружу нужен админке: там он стоит плейсхолдером в пустом поле — видно, что
 * покажется, если ничего не вводить.
 */
export function builtinText(key: TranslationKey | undefined, lang: Lang): string {
  if (!key) return '';
  return DICTIONARIES[lang]?.[key] ?? '';
}

const builtin = builtinText;

/**
 * Текст строки на нужном языке.
 * Порядок фолбэков — в шапке файла; пустая строка считается «не задано».
 */
export function pick(text: LocalizedText, key: TranslationKey | undefined, lang: Lang): string {
  const own = text[lang]?.trim();
  if (own) return own;

  const fallback = text[FALLBACK_LANG]?.trim();
  const dict = builtin(key, lang);
  // Словарь важнее чужого языка: перевод на языке читателя лучше, чем русский
  // текст админа. Наоборот — только когда в словаре ключа нет вовсе.
  if (dict) return dict;
  if (fallback) return fallback;
  return key ?? '';
}

// ── Разрешённый вид: то, что рисует страница ────────────────────────────────

export interface ViewFeature {
  id: string;
  section: string;
  title: string;
  free: Limit;
  pro: Limit;
}

export interface ViewSection {
  id: string;
  title: string;
  hint: string;
  features: ViewFeature[];
}

export interface ViewExtra {
  id: string;
  title: string;
  hint: string;
  price: number;
}

export interface PricingView {
  price: { currency: string; monthly: number; yearly: number };
  /** Скидка годового плана в процентах. */
  yearlyDiscount: number;
  /**
   * Строки, по которым выбирают тариф (`KEY_FEATURES`), — в их собственном
   * порядке и без разделов. Показываются сразу.
   */
  keyFeatures: ViewFeature[];
  /**
   * Всё остальное, по разделам, — под раскрывающимся списком. Главные строки
   * сюда не попадают: строка, показанная дважды, читается как две разные.
   */
  sections: ViewSection[];
  /** Сколько строк спрятано: цифра стоит на самой кнопке «показать всё». */
  restCount: number;
  highlights: Record<PlanId, ViewFeature[]>;
  soon: { id: string; title: string }[];
  extras: ViewExtra[];
  faq: { id: string; question: string; answer: string }[];
  /** Общие тексты страницы: ключ словаря → готовая строка. */
  text(key: TranslationKey): string;
}

/**
 * Документ → то, что рисуется. Вся работа с языками и фолбэками заканчивается
 * здесь: страница дальше видит обычные строки и ничего не знает про документ.
 */
export function resolvePricing(doc: PricingDoc, lang: Lang): PricingView {
  const features = doc.features
    .filter((f) => f.on)
    .map((f) => ({
      id: f.id,
      section: f.section,
      title: pick(f.title, f.titleKey, lang),
      free: f.free,
      pro: f.pro,
    }));

  const byId = new Map(features.map((f) => [f.id, f]));

  // Порядок главных строк задаёт KEY_FEATURES, а не документ: это отдельное
  // высказывание — «вот по этому и выбирают», — и порядок в нём свой.
  // Выключенной или удалённой из документа строки в карте просто нет.
  const keyFeatures = KEY_FEATURES.map((id) => byId.get(id)).filter(
    (f): f is ViewFeature => Boolean(f),
  );
  const isKey = new Set(keyFeatures.map((f) => f.id));

  const rest = features.filter((f) => !isKey.has(f.id));

  const sections = doc.sections
    .map((s) => ({
      id: s.id,
      title: pick(s.title, s.titleKey, lang),
      hint: pick(s.hint, s.hintKey, lang),
      features: rest.filter((f) => f.section === s.id),
    }))
    // Раздел без строк не рисуется: пустой заголовок в таблице ничего не
    // сообщает, а остаться без строк раздел может двумя путями — админ выключил
    // последнюю или все его строки уехали в главное.
    .filter((s) => s.features.length > 0);

  const highlights = {} as Record<PlanId, ViewFeature[]>;
  for (const plan of PLANS) {
    highlights[plan] = (doc.highlights[plan] ?? [])
      .map((id) => byId.get(id))
      .filter((f): f is ViewFeature => Boolean(f));
  }

  const monthly = doc.price.monthly;
  const yearly = doc.price.yearly;
  const full = monthly * 12;

  return {
    price: { ...doc.price },
    yearlyDiscount: full > 0 ? Math.max(0, Math.round((1 - yearly / full) * 100)) : 0,
    keyFeatures,
    sections,
    // Считается по разделам, а не по `rest`: строка выключенного раздела на
    // странице не появится, и обещать её в счётчике нельзя.
    restCount: sections.reduce((n, s) => n + s.features.length, 0),
    highlights,
    soon: doc.soon.map((s) => ({ id: s.id, title: pick(s.title, s.titleKey, lang) })),
    extras: doc.extras.map((e) => ({
      id: e.id,
      price: e.price,
      title: pick(e.title, e.titleKey, lang),
      hint: pick(e.hint, e.hintKey, lang),
    })),
    faq: doc.faq.map((f) => ({
      id: f.id,
      question: pick(f.question, f.questionKey, lang),
      answer: pick(f.answer, f.answerKey, lang),
    })),
    text: (key) => pick(doc.texts[key] ?? {}, key, lang),
  };
}
